import { useState, useEffect, useCallback } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import {
  Ticket, Search, X, Loader2, RefreshCw,
  Phone, MapPin, User, CheckCircle2,
  Clock, Zap, Star, Inbox, XCircle, Plus,
  AlertTriangle, AlertCircle, Timer, Bell
} from 'lucide-react'
import { taskService } from '@/services/firebase/tasks'
import { useAuthStore } from '@/store/authStore'
import { ROUTES } from '@/constants'
import type { Task, TaskStatus } from '@/types'
import toast from 'react-hot-toast'

// ── Constants ─────────────────────────────────────────────────────────────────
const TERMINAL: TaskStatus[] = ['closed', 'cancelled']

const STATUS_CFG: Record<TaskStatus, { label: string; bg: string; text: string; border: string; icon: React.ReactNode; dot: string }> = {
  open:             { label: 'Open',             bg: 'bg-sky-50',     text: 'text-sky-700',     border: 'border-sky-200',     icon: <Inbox size={10} />,        dot: '#0ea5e9' },
  assigned:         { label: 'Assigned',         bg: 'bg-blue-50',    text: 'text-blue-700',    border: 'border-blue-200',    icon: <User size={10} />,         dot: '#3b82f6' },
  in_progress:      { label: 'In Progress',      bg: 'bg-violet-50',  text: 'text-violet-700',  border: 'border-violet-200',  icon: <Zap size={10} />,          dot: '#8b5cf6' },
  site_visit:       { label: 'Site Visit',       bg: 'bg-indigo-50',  text: 'text-indigo-700',  border: 'border-indigo-200',  icon: <MapPin size={10} />,       dot: '#6366f1' },
  work_in_progress: { label: 'Work In Progress', bg: 'bg-orange-50',  text: 'text-orange-700',  border: 'border-orange-200',  icon: <Zap size={10} />,          dot: '#f97316' },
  pending_approval: { label: 'Pending Approval', bg: 'bg-amber-50',   text: 'text-amber-700',   border: 'border-amber-200',   icon: <Clock size={10} />,        dot: '#f59e0b' },
  pending_feedback: { label: 'Pending Feedback', bg: 'bg-purple-50',  text: 'text-purple-700',  border: 'border-purple-200',  icon: <Star size={10} />,         dot: '#a855f7' },
  closed:           { label: 'Closed',           bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200', icon: <CheckCircle2 size={10} />, dot: '#10b981' },
  cancelled:        { label: 'Cancelled',        bg: 'bg-gray-50',    text: 'text-gray-500',    border: 'border-gray-200',    icon: <XCircle size={10} />,      dot: '#9ca3af' },
}

const STATUS_THEME: Record<TaskStatus, { color: string; shadow: string }> = {
  open:             { color: '#0ea5e9', shadow: 'rgba(14,165,233,0.08)' },
  assigned:         { color: '#3b82f6', shadow: 'rgba(59,130,246,0.08)' },
  in_progress:      { color: '#8b5cf6', shadow: 'rgba(139,92,246,0.08)' },
  site_visit:       { color: '#6366f1', shadow: 'rgba(99,102,241,0.08)' },
  work_in_progress: { color: '#f97316', shadow: 'rgba(249,115,22,0.08)' },
  pending_approval: { color: '#f59e0b', shadow: 'rgba(245,158,11,0.15)' },
  pending_feedback: { color: '#a855f7', shadow: 'rgba(168,85,247,0.08)' },
  closed:           { color: '#10b981', shadow: 'rgba(16,185,129,0.06)' },
  cancelled:        { color: '#9ca3af', shadow: 'rgba(156,163,175,0.04)' },
}

const PRIORITY_CFG: Record<string, { bar: string; badge: string; text: string }> = {
  low:    { bar: '#94a3b8', badge: 'bg-gray-100',   text: 'text-gray-500'   },
  medium: { bar: '#3b82f6', badge: 'bg-blue-100',   text: 'text-blue-700'   },
  high:   { bar: '#f97316', badge: 'bg-orange-100', text: 'text-orange-700' },
  urgent: { bar: '#ef4444', badge: 'bg-red-100',    text: 'text-red-700'    },
}

// ── Attention logic ───────────────────────────────────────────────────────────
type AttentionReason = 'stalled' | 'urgent_open' | 'pending_approval'

const getAttentionReasons = (task: Task, now: number): AttentionReason[] => {
  const reasons: AttentionReason[] = []
  if (TERMINAL.includes(task.status)) return reasons
  const stalledHrs = (now - task.updatedAt.getTime()) / (60 * 60 * 1000)
  if (stalledHrs >= 48) reasons.push('stalled')
  if (task.status === 'pending_approval') reasons.push('pending_approval')
  if (task.priority === 'urgent' && task.status === 'open') reasons.push('urgent_open')
  return reasons
}

const ATTENTION_CFG: Record<AttentionReason, { label: string; icon: React.ElementType; color: string; bg: string; border: string }> = {
  stalled:          { label: 'Stalled 48h+',       icon: Timer,         color: 'text-orange-700', bg: 'bg-orange-50',  border: 'border-orange-200' },
  pending_approval: { label: 'Needs Approval',      icon: AlertCircle,   color: 'text-amber-700',  bg: 'bg-amber-50',   border: 'border-amber-200'  },
  urgent_open:      { label: 'Urgent — Unassigned', icon: AlertTriangle, color: 'text-red-700',    bg: 'bg-red-50',     border: 'border-red-200'    },
}

// Format stale duration nicely
const formatStaleDuration = (updatedAt: Date, now: number) => {
  const ms = now - updatedAt.getTime()
  const hrs = Math.floor(ms / (60 * 60 * 1000))
  if (hrs < 24) return `${hrs}h ago`
  const days = Math.floor(hrs / 24)
  return `${days}d ${hrs % 24}h ago`
}

// ── Ticket card ───────────────────────────────────────────────────────────────
const TicketCard = ({ task, now, onClick }: { task: Task; now: number; onClick: () => void }) => {
  const s = STATUS_CFG[task.status]
  const p = PRIORITY_CFG[task.priority] ?? PRIORITY_CFG.medium
  const reasons = getAttentionReasons(task, now)
  const isAttention = reasons.length > 0
  const initials = task.customerName.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase()
  const staleHrs = Math.floor((now - task.updatedAt.getTime()) / (60 * 60 * 1000))

  const theme = STATUS_THEME[task.status]

  return (
    <div onClick={onClick}
      className="group bg-white rounded-3xl overflow-hidden cursor-pointer transition-all duration-200 hover:-translate-y-0.5 active:scale-[0.98] border-l-[4px]"
      style={{
        boxShadow: isAttention
          ? '0 2px 16px rgba(239,68,68,0.15), 0 0 0 1.5px rgba(239,68,68,0.22)'
          : `0 2px 12px ${theme.shadow}, 0 0 0 1px rgba(0,0,0,0.04)`,
        borderLeftColor: theme.color
      }}>

      {/* Dark gradient header */}
      <div className="relative px-4 pt-4 pb-3 overflow-hidden"
        style={{ background: isAttention ? 'linear-gradient(135deg,#1a0505 0%,#280a0a 100%)' : 'linear-gradient(135deg,#050d1f 0%,#0d1f3c 100%)' }}>
        <div className="absolute top-0 left-0 right-0 h-[3px]"
          style={{ background: isAttention ? '#ef4444' : p.bar }} />
        <div className="absolute -top-6 -right-6 w-28 h-28 rounded-full pointer-events-none"
          style={{ background: `radial-gradient(circle,${isAttention ? 'rgba(239,68,68,0.4)' : 'rgba(249,115,22,0.35)'} 0%,transparent 70%)`, filter: 'blur(16px)' }} />

        {isAttention && (
          <div className="relative z-10 flex flex-wrap gap-1.5 mb-3">
            {reasons.map(r => {
              const cfg = ATTENTION_CFG[r]
              return (
                <span key={r} className={`inline-flex items-center gap-1 text-[10px] font-black px-2 py-0.5 rounded-full border ${cfg.bg} ${cfg.color} ${cfg.border}`}>
                  <cfg.icon size={9} /> {cfg.label}
                  {r === 'stalled' && <span className="ml-0.5 opacity-70">· {formatStaleDuration(task.updatedAt, now)}</span>}
                </span>
              )
            })}
          </div>
        )}

        <div className="relative z-10 flex items-start justify-between gap-2">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-11 h-11 rounded-2xl shrink-0 flex items-center justify-center text-white text-sm font-black"
              style={{ background: isAttention ? 'linear-gradient(135deg,#ef4444,#f97316)' : 'linear-gradient(135deg,#f97316,#ef4444)', boxShadow: isAttention ? '0 4px 12px rgba(239,68,68,0.5)' : '0 4px 12px rgba(249,115,22,0.45)' }}>
              {initials}
            </div>
            <div className="min-w-0">
              <p className="font-black text-white text-[15px] truncate leading-tight">{task.customerName}</p>
              <p className="text-[10px] font-mono text-orange-300/70 mt-0.5 tracking-wider">{task.taskNumber}</p>
            </div>
          </div>
          <span className={`shrink-0 flex items-center gap-1 text-[10px] font-black px-2.5 py-1 rounded-full border ${s.bg} ${s.text} ${s.border}`}>
            {s.icon} {s.label}
          </span>
        </div>

        <div className="relative z-10 flex items-center gap-2 mt-3">
          <span className={`text-[10px] font-black px-2.5 py-1 rounded-full ${p.badge} ${p.text} capitalize`}>
            {task.priority}
          </span>
          {!TERMINAL.includes(task.status) && (
            <span className="text-[10px] px-2.5 py-1 rounded-full font-medium"
              style={{ background: staleHrs >= 48 ? 'rgba(239,68,68,0.2)' : 'rgba(255,255,255,0.08)', color: staleHrs >= 48 ? '#fca5a5' : 'rgba(255,255,255,0.45)' }}>
              {formatStaleDuration(task.updatedAt, now)}
            </span>
          )}
        </div>
      </div>

      {/* Body */}
      <div className="px-4 py-3 space-y-1.5">
        <div className="flex items-center gap-2 text-[12px] text-gray-600">
          <Phone size={11} className="text-gray-400 shrink-0" />
          <span className="font-semibold">+91 {task.customerPhone}</span>
        </div>
        <div className="flex items-center gap-2 text-[12px] text-gray-500 truncate">
          <MapPin size={11} className="text-gray-400 shrink-0" />
          <span className="truncate">{task.address}{task.city ? `, ${task.city}` : ''}</span>
        </div>
        {task.assignedEngineerName && (
          <div className="flex items-center gap-2 text-[11px] text-blue-600">
            <User size={11} className="shrink-0" />
            <span className="truncate font-semibold">{task.assignedEngineerName}</span>
          </div>
        )}
        {task.description && (
          <p className="text-[11px] text-gray-400 line-clamp-2 leading-relaxed italic">"{task.description}"</p>
        )}
      </div>
    </div>
  )
}

// ── Main page ─────────────────────────────────────────────────────────────────
type TabValue = 'all' | 'attention' | TaskStatus

const STATUS_FILTERS: { label: string; value: TabValue }[] = [
  { label: 'All',      value: 'all'              },
  { label: 'Open',     value: 'open'             },
  { label: 'Active',   value: 'in_progress'      },
  { label: 'Approval', value: 'pending_approval' },
  { label: 'Closed',   value: 'closed'           },
]

const TicketListPage = () => {
  const { user } = useAuthStore()
  const navigate = useNavigate()
  const location = useLocation()
  const isAdmin = user?.role === 'admin'

  const [tasks, setTasks]     = useState<Task[]>([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch]   = useState('')
  const [tab, setTab]         = useState<TabValue>(
    (location.state?.status as TabValue) || 'all'
  )
  const [now, setNow]         = useState(() => Date.now())

  useEffect(() => {
    if (location.state?.status) {
      setTab(location.state.status as TabValue)
    }
  }, [location.state?.status])

  const load = useCallback(async () => {
    setLoading(true)
    const res = await taskService.list()
    if (res.success && res.data) {
      setTasks(res.data.filter(t => t.type === 'support'))
    } else {
      toast.error('Failed to load tickets')
    }
    setLoading(false)
    setNow(Date.now())
  }, [])

  useEffect(() => { load() }, [load])

  // Refresh 'now' every minute so stale timers update without a full reload
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 60_000)
    return () => clearInterval(id)
  }, [])

  const attentionTasks = tasks.filter(t => getAttentionReasons(t, now).length > 0)

  const filtered = tasks.filter(t => {
    const matchTab = tab === 'all'
      ? true
      : tab === 'attention'
        ? getAttentionReasons(t, now).length > 0
        : tab === 'in_progress'
          ? ['assigned', 'in_progress', 'site_visit', 'work_in_progress', 'pending_feedback'].includes(t.status)
          : t.status === tab
    const q = search.toLowerCase()
    const matchSearch = !q || [t.customerName, t.customerPhone, t.taskNumber, t.address, t.city, t.assignedEngineerName, t.description]
      .some(v => v?.toLowerCase().includes(q))
    return matchTab && matchSearch
  })

  const total   = tasks.length
  const open    = tasks.filter(t => t.status === 'open').length
  const active  = tasks.filter(t => ['assigned','in_progress','site_visit','work_in_progress','pending_feedback'].includes(t.status)).length
  const closed  = tasks.filter(t => t.status === 'closed').length
  const attnCnt = attentionTasks.length

  return (
    <div className="space-y-5 pb-10">

      {/* ── Hero ── */}
      <div className="relative overflow-hidden rounded-3xl text-white"
        style={{ background: 'linear-gradient(135deg,#050d1f 0%,#0c1e3d 50%,#0d2a50 100%)' }}>
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute -top-10 -right-10 w-56 h-56 rounded-full"
            style={{ background: 'radial-gradient(circle,rgba(239,68,68,0.3) 0%,transparent 70%)', filter: 'blur(40px)' }} />
          <div className="absolute bottom-0 left-1/3 w-40 h-28 rounded-full"
            style={{ background: 'radial-gradient(circle,rgba(249,115,22,0.2) 0%,transparent 70%)', filter: 'blur(30px)' }} />
        </div>
        <div className="relative z-10 p-5 lg:p-7 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <div className="inline-flex items-center gap-2 bg-white/10 border border-white/10 rounded-full px-3 py-1 mb-3">
              <Ticket size={11} className="text-red-300" />
              <span className="text-[11px] text-blue-200 font-semibold uppercase tracking-wider">Support Tickets</span>
            </div>
            <h1 className="text-xl lg:text-2xl font-black">Tickets</h1>
            <p className="text-blue-300/70 text-sm mt-1">
              {total} ticket{total !== 1 ? 's' : ''} · {active} active
              {attnCnt > 0 && <span className="ml-2 text-red-300 font-black">· {attnCnt} need attention</span>}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {isAdmin && (
              <button onClick={() => navigate(ROUTES.NEW_TASK, { state: { type: 'support' } })}
                className="flex items-center gap-2 px-4 py-2.5 rounded-2xl text-sm font-black transition-all hover:scale-[1.03]"
                style={{ background: 'linear-gradient(135deg,#ef4444,#f97316)', boxShadow: '0 4px 16px rgba(239,68,68,0.3)' }}>
                <Plus size={15} /> New Ticket
              </button>
            )}
            <button onClick={load}
              className="p-2.5 bg-white/10 rounded-xl hover:bg-white/20 transition-all" title="Refresh">
              <RefreshCw size={15} className={`text-blue-200 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>
      </div>

      {/* ── Stats bar ── */}
      <div className="grid grid-cols-5 gap-3">
        {[
          { label: 'Total',     value: total,   color: 'text-gray-800',    bg: 'bg-white'      },
          { label: 'Open',      value: open,    color: 'text-sky-700',     bg: 'bg-sky-50'     },
          { label: 'Active',    value: active,  color: 'text-violet-700',  bg: 'bg-violet-50'  },
          { label: 'Closed',    value: closed,  color: 'text-emerald-700', bg: 'bg-emerald-50' },
          { label: '⚠ Attention', value: attnCnt, color: attnCnt > 0 ? 'text-red-600' : 'text-gray-400', bg: attnCnt > 0 ? 'bg-red-50' : 'bg-white' },
        ].map(m => (
          <div key={m.label} className={`${m.bg} rounded-2xl px-3 py-3 text-center`}
            style={{ boxShadow: '0 1px 4px rgba(0,0,0,0.05), 0 0 0 1px rgba(0,0,0,0.04)' }}>
            <p className={`text-xl font-black ${m.color}`}>{m.value}</p>
            <p className="text-[9px] font-bold text-gray-400 mt-0.5">{m.label}</p>
          </div>
        ))}
      </div>

      {/* ── Attention banner (if any) ── */}
      {attnCnt > 0 && tab !== 'attention' && (
        <button onClick={() => setTab('attention')}
          className="w-full flex items-center gap-3 px-4 py-3.5 rounded-2xl text-left transition-all hover:scale-[1.005]"
          style={{ background: 'linear-gradient(135deg,rgba(239,68,68,0.08),rgba(249,115,22,0.06))', border: '1.5px solid rgba(239,68,68,0.2)' }}>
          <div className="w-8 h-8 rounded-xl flex items-center justify-center shrink-0 bg-red-100">
            <Bell size={14} className="text-red-600" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-black text-red-700">
              {attnCnt} ticket{attnCnt !== 1 ? 's' : ''} need your attention
            </p>
            <p className="text-xs text-red-500/80 mt-0.5">
              Stalled &gt;48h, urgent unassigned, or awaiting admin approval
            </p>
          </div>
          <span className="text-xs font-black text-red-600 bg-red-100 px-2.5 py-1 rounded-full shrink-0">View →</span>
        </button>
      )}

      {/* ── Search ── */}
      <div className="relative">
        <Search size={14} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
        <input value={search} onChange={e => setSearch(e.target.value)}
          placeholder="Search customer, phone, task ID, description…"
          className="w-full pl-9 pr-9 py-2.5 text-sm bg-white border border-gray-200 rounded-2xl outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-400 transition-all text-gray-700" />
        {search && (
          <button onClick={() => setSearch('')}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
            <X size={13} />
          </button>
        )}
      </div>

      {/* ── Filter tabs ── */}
      <div className="-mx-4 flex gap-2 overflow-x-auto scrollbar-hide px-4 pb-1">
        <button onClick={() => setTab('attention')}
          className="shrink-0 flex items-center gap-1.5 text-[11px] font-black px-3.5 py-2 rounded-2xl transition-all active:scale-95"
          style={tab === 'attention'
            ? { background: 'linear-gradient(135deg,#ef4444,#f97316)', color: '#fff', boxShadow: '0 4px 12px rgba(239,68,68,0.35)' }
            : attnCnt > 0
              ? { background: '#fef2f2', color: '#b91c1c', border: '1px solid #fecaca' }
              : { background: '#f9fafb', color: '#9ca3af', border: '1px solid #e5e7eb' }}>
          <AlertTriangle size={10} />
          Attention
          {attnCnt > 0 && (
            <span className="text-[9px] px-1.5 py-0.5 rounded-full font-black"
              style={{ background: tab === 'attention' ? 'rgba(255,255,255,0.2)' : '#fee2e2', color: tab === 'attention' ? '#fff' : '#b91c1c' }}>
              {attnCnt}
            </span>
          )}
        </button>
        {STATUS_FILTERS.map(f => {
          const cnt = f.value === 'all'
            ? tasks.length
            : f.value === 'in_progress'
              ? tasks.filter(t => ['assigned', 'in_progress', 'site_visit', 'work_in_progress', 'pending_feedback'].includes(t.status)).length
              : tasks.filter(t => t.status === f.value).length
          return (
            <button key={f.value} onClick={() => setTab(f.value)}
              className="shrink-0 flex items-center gap-1.5 text-[11px] font-black px-3.5 py-2 rounded-2xl transition-all active:scale-95"
              style={tab === f.value
                ? { background: 'linear-gradient(135deg,#1e293b,#0f172a)', color: '#fff', boxShadow: '0 4px 12px rgba(15,23,42,0.25)' }
                : { background: '#f9fafb', color: '#6b7280', border: '1px solid #e5e7eb' }}>
              {f.label}
              {cnt > 0 && (
                <span className="text-[9px] px-1.5 py-0.5 rounded-full font-black"
                  style={{ background: tab === f.value ? 'rgba(255,255,255,0.15)' : '#e5e7eb', color: tab === f.value ? '#fff' : '#4b5563' }}>
                  {cnt}
                </span>
              )}
            </button>
          )
        })}
      </div>

      {/* ── Attention reasons legend ── */}
      {tab === 'attention' && (
        <div className="flex flex-wrap gap-2">
          {(Object.entries(ATTENTION_CFG) as [AttentionReason, typeof ATTENTION_CFG[AttentionReason]][]).map(([key, cfg]) => (
            <span key={key}
              className={`inline-flex items-center gap-1.5 text-[10px] font-bold px-2.5 py-1.5 rounded-xl border ${cfg.bg} ${cfg.color} ${cfg.border}`}>
              <cfg.icon size={10} /> {cfg.label}
            </span>
          ))}
        </div>
      )}

      {/* ── Content ── */}
      {loading ? (
        <div className="flex flex-col items-center justify-center py-24 gap-3">
          <div className="w-14 h-14 rounded-3xl flex items-center justify-center"
            style={{ background: 'linear-gradient(135deg,rgba(239,68,68,0.1),rgba(249,115,22,0.08))' }}>
            <Loader2 size={22} className="text-red-500 animate-spin" />
          </div>
          <p className="text-sm text-gray-400 font-medium">Loading tickets…</p>
        </div>
      ) : filtered.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-24 gap-3">
          <div className="w-16 h-16 rounded-3xl flex items-center justify-center bg-gray-50">
            {tab === 'attention'
              ? <AlertTriangle size={24} className="text-gray-300" />
              : <Ticket size={24} className="text-gray-300" />}
          </div>
          <p className="text-sm font-bold text-gray-500">
            {tab === 'attention' ? 'No tickets need attention' : 'No tickets found'}
          </p>
          <p className="text-xs text-gray-400">
            {search ? 'Try a different search term'
              : tab === 'attention' ? 'All tickets are within the 48h activity window'
              : tab !== 'all' ? 'No tickets in this status'
              : 'Create a new support ticket to get started'}
          </p>
          {isAdmin && !search && tab === 'all' && (
            <button onClick={() => navigate(ROUTES.NEW_TASK, { state: { type: 'support' } })}
              className="flex items-center gap-2 mt-2 px-4 py-2.5 text-sm font-black text-white rounded-2xl transition-all hover:scale-[1.02]"
              style={{ background: 'linear-gradient(135deg,#ef4444,#f97316)' }}>
              <Plus size={14} /> New Ticket
            </button>
          )}
        </div>
      ) : (
        <>
          <p className="text-xs text-gray-400 font-medium">
            {filtered.length} ticket{filtered.length !== 1 ? 's' : ''}
            {tab === 'attention' && ' need attention'}
            {tab !== 'all' && tab !== 'attention' && ` · ${STATUS_CFG[tab as TaskStatus]?.label}`}
            {search && ` matching "${search}"`}
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {filtered.map(task => (
              <TicketCard key={task.id} task={task} now={now}
                onClick={() => navigate(`/tasks/${task.id}`)} />
            ))}
          </div>
        </>
      )}
    </div>
  )
}

export default TicketListPage
